import {
  ArrowLeftRight,
  BatteryCharging,
  BookOpen,
  Boxes,
  Building2,
  CalendarClock,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronsUpDown,
  ClipboardCheck,
  CreditCard,
  Fan,
  FileText,
  FolderTree,
  Gauge,
  GraduationCap,
  Hammer,
  House,
  Inbox,
  Laptop,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MapPin,
  Menu,
  MonitorSmartphone,
  Package,
  Plug,
  ScanBarcode,
  School,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Timer,
  UserMinus,
  UserCog,
  Users,
  Workflow,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePlan, type BillingFeature } from '../lib/billing';
import { usePublicSchool } from '../lib/branding';
import { FACILITIES_ROLE_LABEL } from '../lib/facilities';
import { initials, ROLE_LABEL } from '../lib/format';
import { currentSchool, schoolHref } from '../lib/school';
import type { TenantRole } from '../lib/types';
import { ConfirmEmailBanner } from './account-banners';
import { NotificationBell } from './NotificationBell';
import { PaymentDueBanner, TrialBanner } from './plan';
import { cn } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  minRole?: TenantRole;
  /** Outside the current school, so it loads as a full page. */
  external?: boolean;
  /** Only in the requester portal: for people who aren't staff on either help desk. */
  portal?: boolean;
  /** The knowledgebase: always there for staff, and for everyone else once there's an article they can read. */
  knowledge?: boolean;
  /** Plan & billing: once the deployment bills schools. */
  billing?: boolean;
  /** Only when the school's plan includes this add-on. */
  feature?: BillingFeature;
}

interface NavSection {
  section?: string;
  /** Items are checked against the facilities role; the section only shows in schools with Facilities. */
  facilities?: boolean;
  items: NavItem[];
}

// Every item names its minimum role; requesters only see the help desks and their account settings.
const SCHOOL_NAV: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, minRole: 'VIEWER' },
      { to: '/', label: 'Home', icon: House, end: true, portal: true },
    ],
  },
  {
    section: 'Inventory',
    items: [
      { to: '/assets', label: 'Assets', icon: Boxes, minRole: 'VIEWER' },
      { to: '/carts', label: 'Carts', icon: BatteryCharging, minRole: 'VIEWER' },
      { to: '/parts', label: 'Parts', icon: Package, minRole: 'VIEWER' },
      { to: '/station', label: 'Check in / out', icon: ScanBarcode, minRole: 'TECHNICIAN' },
      { to: '/verify', label: 'Verify assets', icon: ClipboardCheck, minRole: 'TECHNICIAN' },
      { to: '/checkouts', label: 'Checkouts', icon: ArrowLeftRight, minRole: 'VIEWER', end: true },
      { to: '/loaners', label: 'Loaners', icon: Timer, minRole: 'VIEWER' },
      { to: '/checkouts/bulk', label: 'Rollouts & returns', icon: GraduationCap, minRole: 'TECHNICIAN' },
      { to: '/departures', label: 'Departures', icon: UserMinus, minRole: 'VIEWER' },
      { to: '/incidents', label: 'Incidents & billing', icon: ShieldAlert, minRole: 'TECHNICIAN' },
    ],
  },
  {
    section: 'Help desk',
    items: [
      { to: '/work-orders', label: 'Work orders', icon: Wrench, minRole: 'VIEWER' },
      { to: '/requests', label: 'My requests', icon: LifeBuoy, minRole: 'REQUESTER' },
      { to: '/events', label: 'Event requests', icon: CalendarDays, minRole: 'REQUESTER' },
      { to: '/knowledge', label: 'Knowledgebase', icon: BookOpen, minRole: 'REQUESTER', knowledge: true },
      { to: '/reports', label: 'Reports', icon: ChartColumn, minRole: 'VIEWER' },
      { to: '/automation', label: 'Automation', icon: Workflow, minRole: 'TECHNICIAN' },
      { to: '/helpdesk-setup', label: 'Help desk setup', icon: FolderTree, minRole: 'TECHNICIAN' },
    ],
  },
  {
    section: 'Facilities',
    facilities: true,
    items: [
      { to: '/facilities', label: 'Overview', icon: Gauge, end: true, minRole: 'VIEWER' },
      { to: '/facilities/work-orders', label: 'Work orders', icon: Hammer, minRole: 'VIEWER' },
      { to: '/facilities/requests', label: 'My requests', icon: LifeBuoy, minRole: 'REQUESTER' },
      { to: '/facilities/maintenance', label: 'Maintenance', icon: CalendarClock, minRole: 'VIEWER' },
      { to: '/facilities/inspections', label: 'Inspections', icon: ClipboardCheck, minRole: 'VIEWER' },
      { to: '/facilities/equipment', label: 'Equipment', icon: Fan, minRole: 'VIEWER' },
      { to: '/facilities/documents', label: 'Documents', icon: FileText, minRole: 'VIEWER' },
      { to: '/facilities/reports', label: 'Reports', icon: ChartColumn, minRole: 'VIEWER' },
      { to: '/facilities/setup', label: 'Facilities setup', icon: FolderTree, minRole: 'TECHNICIAN' },
    ],
  },
  {
    section: 'Directory',
    items: [
      { to: '/people', label: 'People', icon: Users, minRole: 'VIEWER' },
      { to: '/locations', label: 'Locations', icon: Building2, minRole: 'VIEWER' },
    ],
  },
  {
    section: 'Devices',
    items: [
      { to: '/devices', label: 'ChromeOS devices', icon: Laptop, minRole: 'VIEWER' },
      { to: '/managed-devices', label: 'Managed devices', icon: MonitorSmartphone, minRole: 'VIEWER' },
      { to: '/device-location', label: 'Chromebook location', icon: MapPin, minRole: 'VIEWER', feature: 'DEVICE_LOCATION' },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/integrations', label: 'Integrations', icon: Plug, minRole: 'TECHNICIAN' },
      { to: '/settings', label: 'Settings', icon: Settings, minRole: 'REQUESTER' },
      { to: '/billing', label: 'Plan & billing', icon: CreditCard, minRole: 'ADMIN', billing: true },
    ],
  },
];

export function Logo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 64 64" className={cn('size-9', className)} aria-hidden>
      <rect width="64" height="64" rx="14" className="fill-brand-700" />
      <path d="M18 40.5 32 47l14-6.5V27L32 20.5 18 27z" fill="none" className="stroke-brand-200" strokeWidth="4" strokeLinejoin="round" />
      <path d="M18 27l14 6.5L46 27M32 33.5V47" fill="none" className="stroke-white" strokeWidth="4" strokeLinejoin="round" />
    </svg>
  );
}

function useOutsideClick(ref: React.RefObject<HTMLElement | null>, onOutside: () => void) {
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onOutside();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onOutside]);
}

function SchoolSwitcher() {
  const { me, switchSchool } = useAuth();
  const school = usePublicSchool();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(ref, () => setOpen(false));

  if (!me || me.memberships.length === 0) return null;
  const canSwitch = me.memberships.length > 1 || !me.tenant;
  const logo = school.data?.logoUrl;
  const roleLabel = me.role ? ROLE_LABEL[me.role] : me.user.isPlatformAdmin ? 'Platform admin' : 'No access';
  // School administrators are facilities administrators too, so only a separate facilities role is worth showing.
  const facilitiesLabel = me.facilitiesRole && me.facilitiesRole !== 'REQUESTER' && me.role !== 'ADMIN' ? FACILITIES_ROLE_LABEL[me.facilitiesRole] : null;

  return (
    <div ref={ref} className="relative px-3 pb-3">
      <button
        onClick={() => canSwitch && setOpen((value) => !value)}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg bg-white/5 px-3 py-2.5 text-left ring-1 ring-white/10',
          canSwitch && 'hover:bg-white/10',
        )}
      >
        {logo && me.tenant ? (
          <img src={logo} alt="" className="size-7 shrink-0 rounded-md bg-white object-contain p-0.5" />
        ) : (
          <School className="size-4 shrink-0 text-brand-300" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{me.tenant?.name ?? 'Choose a school'}</p>
          <p className="truncate text-xs text-brand-300" title={facilitiesLabel ? `${roleLabel} · ${facilitiesLabel}` : roleLabel}>
            {roleLabel}
            {facilitiesLabel && ` · ${facilitiesLabel}`}
          </p>
        </div>
        {canSwitch && <ChevronsUpDown className="size-4 shrink-0 text-brand-300" />}
      </button>
      {open && (
        <div className="absolute inset-x-3 top-full z-40 mt-1 max-h-80 overflow-auto rounded-lg border border-slate-200 bg-white py-1 text-slate-800 shadow-xl">
          {me.memberships.map((membership) => (
            <button
              key={membership.tenantId}
              onClick={() => {
                setOpen(false);
                switchSchool(membership.slug);
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{membership.name}</p>
                <p className="text-xs text-slate-500">
                  /{membership.slug} · {ROLE_LABEL[membership.role]}
                  {membership.status === 'SUSPENDED' && ' · Suspended'}
                </p>
              </div>
              {membership.slug === currentSchool && <Check className="size-4 text-brand-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const navClass = (active: boolean) =>
  cn(
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    active ? 'bg-white/10 text-white' : 'text-brand-100/75 hover:bg-white/5 hover:text-white',
  );

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { me, can, canFacilities, logout } = useAuth();
  const plan = usePlan();
  const inSchool = !!currentSchool && !!me?.tenant;
  const knowledge = useQuery({
    queryKey: ['knowledge', 'summary'],
    queryFn: () => api.get<{ count: number; canWrite: boolean }>('/knowledge/summary'),
    enabled: inSchool && !can('VIEWER'),
    staleTime: 60_000,
  });
  if (!me) return null;

  const sections: NavSection[] =
    currentSchool && me.tenant
      ? SCHOOL_NAV.map((section) => ({
          ...section,
          items: section.items.filter(
            (item) =>
              (!item.billing || plan.visible) &&
              (!item.feature || plan.has(item.feature)) &&
              (item.portal
                ? !can('VIEWER') && !canFacilities('VIEWER')
                : item.knowledge
                  ? can('VIEWER') || (can('REQUESTER') && (knowledge.data?.count ?? 0) > 0)
                  : !item.minRole || (section.facilities ? canFacilities(item.minRole) : can(item.minRole))),
          ),
        })).filter((section) => section.items.length > 0)
      : [];
  if (me.user.isPlatformAdmin) {
    sections.push({
      section: 'Edvora platform',
      items: [
        { to: '/platform', label: 'Schools', icon: ShieldCheck, end: true, external: !!currentSchool },
        { to: '/platform/stripe', label: 'Stripe', icon: CreditCard, external: !!currentSchool },
        { to: '/platform/admins', label: 'Edvora admins', icon: UserCog, external: !!currentSchool },
        { to: '/platform/integration-requests', label: 'Integration requests', icon: Inbox, external: !!currentSchool },
      ],
    });
  }

  return (
    <aside className="flex h-full w-64 flex-col bg-brand-950 text-brand-100">
      <Link to="/" onClick={onNavigate} className="flex h-16 shrink-0 items-center gap-2.5 px-5">
        <Logo />
        <div className="leading-tight">
          <p className="text-sm font-semibold text-white">Edvora</p>
          <p className="text-[11px] font-medium tracking-wider text-brand-300 uppercase">Campus Asset</p>
        </div>
      </Link>

      <SchoolSwitcher />

      <nav className="scrollbar-dark flex-1 space-y-5 overflow-y-auto px-3 py-2">
        {sections.map((section, index) => (
          <div key={section.section ?? index}>
            {section.section && (
              <p className="mb-1.5 px-3 text-[11px] font-semibold tracking-wider text-brand-400/80 uppercase">{section.section}</p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) =>
                item.external ? (
                  <a key={item.to} href={item.to} className={navClass(false)}>
                    <item.icon className="size-4 shrink-0" />
                    {item.label}
                  </a>
                ) : (
                  <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} className={({ isActive }) => navClass(isActive)}>
                    <item.icon className="size-4 shrink-0" />
                    {item.label}
                  </NavLink>
                ),
              )}
            </div>
          </div>
        ))}
      </nav>

      <div className="flex items-center gap-3 border-t border-white/10 px-4 py-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-700 text-xs font-semibold text-white">
          {initials(me.user.name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">{me.user.name}</p>
          <p className="truncate text-xs text-brand-300">{me.user.email}</p>
        </div>
        {me.tenant && currentSchool && <NotificationBell />}
        <button
          onClick={async () => {
            await logout();
            // A full load clears everything the signed-in account could see.
            window.location.assign(currentSchool ? schoolHref(currentSchool, '/login') : '/login');
          }}
          className="rounded-md p-1.5 text-brand-300 hover:bg-white/10 hover:text-white"
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut className="size-4" />
        </button>
      </div>
    </aside>
  );
}

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { me } = useAuth();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen">
      <div className="fixed inset-y-0 left-0 z-30 hidden lg:block print:hidden">
        <Sidebar />
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <div className="relative h-full w-64">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 -right-10 rounded-md p-1 text-white"
              aria-label="Close menu"
            >
              <X className="size-6" />
            </button>
          </div>
        </div>
      )}

      <div className="lg:pl-64 print:pl-0">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:hidden print:hidden">
          <button onClick={() => setMobileOpen(true)} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100" aria-label="Open menu">
            <Menu className="size-5" />
          </button>
          <Logo className="size-7" />
          <span className="truncate text-sm font-semibold">{me?.tenant?.name ?? 'Edvora Campus Asset'}</span>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <ConfirmEmailBanner />
          {currentSchool && me?.tenant && <PaymentDueBanner />}
          {currentSchool && me?.tenant && <TrialBanner />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
